import {
  ActivityGroup,
  ActivityValue,
  FlatActivityModel,
  FlatInteractionModelData,
  LegacyInteractionModel,
  LegacyStep,
  NestableGroupValue
} from 'types/shared'

import { v4 as uuidv4 } from 'uuid'

const removeUndefinedProperties = <T extends object>(input: T): T =>
  Object.fromEntries(
    Object.entries(input).filter(([_, value]) => value !== undefined)
  ) as T

const activityGroupModelToFlatEntries = (
  activityModel: ActivityGroup
): [string, FlatActivityModel][] => {
  const groupActivityModel = {
    ...activityModel,
    activity_models: (activityModel.activity_models[0] || []).map(
      (groupActivity) => groupActivity.uuid
    )
  } as FlatActivityModel

  const activityModelsInGroup = (
    activityModel.activity_models[0] || []
  ).flatMap(({ value: _ignored, ...groupActivity }) => {
    if (groupActivity.content_type === 'activity_group') {
      return activityGroupModelToFlatEntries(groupActivity as ActivityGroup)
    }

    return [
      [groupActivity.uuid, { ...groupActivity, uuid: groupActivity.uuid }]
    ]
  }) as [string, FlatActivityModel][]

  return [[activityModel.uuid, groupActivityModel], ...activityModelsInGroup]
}

const flatActivityModelsFromSteps = (
  steps: LegacyStep[]
): FlatInteractionModelData['activity_models'] => {
  const flatActivityModelsEntries: [string, FlatActivityModel][] =
    steps.flatMap((item) => {
      const entries: [string, FlatActivityModel][] =
        item.activity_models.flatMap(
          ({ value: _ignored, ...activityModel }) => {
            if (activityModel.content_type === 'activity_group') {
              return activityGroupModelToFlatEntries(activityModel)
            }

            return [[activityModel.uuid, activityModel]]
          }
        )

      return entries
    })

  const flatActivityModels = Object.fromEntries(flatActivityModelsEntries)

  return flatActivityModels
}

const extractActivityValuesFromGroup = (
  activityModels: ActivityGroup['activity_models'][0]
): NestableGroupValue[0] => {
  return {
    uuid: uuidv4(),
    values: Object.fromEntries(
      (activityModels || []).flatMap(
        (
          activityModelInGroup
        ): [string, ActivityValue | NestableGroupValue][] => {
          if (activityModelInGroup.content_type === 'activity_group') {
            const nestedGroupValues = (
              activityModelInGroup as ActivityGroup
            ).activity_models.map((nestedGroup) =>
              extractActivityValuesFromGroup(nestedGroup)
            )
            return [[activityModelInGroup.uuid, nestedGroupValues]]
          }

          if (activityModelInGroup.value === undefined) {
            return []
          }

          return [[activityModelInGroup.uuid, activityModelInGroup.value]]
        }
      )
    )
  }
}

const flatActivityValuesFromSteps = (
  steps: LegacyStep[]
): FlatInteractionModelData['activity_values'] => {
  const flatActivityValuesEntries: [
    string,
    FlatInteractionModelData['activity_values']['']
  ][] = steps.flatMap((item) => {
    const entries: [string, FlatInteractionModelData['activity_values']['']][] =
      item.activity_models.flatMap((activityModel) => {
        if (activityModel.value !== undefined) {
          return [
            [
              activityModel.uuid,
              activityModel.value as FlatInteractionModelData['activity_values']['']
            ]
          ]
        }

        if (
          activityModel.content_type === 'activity_group' &&
          activityModel.activity_models.length
        ) {
          const groupedActivityValues: NestableGroupValue =
            activityModel.activity_models.map((groupedActivityModels) =>
              extractActivityValuesFromGroup(groupedActivityModels)
            )

          return [[activityModel.uuid, groupedActivityValues]]
        }

        return []
      })

    return entries
  })

  const flatActivityValues = Object.fromEntries(flatActivityValuesEntries)

  return flatActivityValues
}

const isLegacyInteractionModel = (
  data: LegacyInteractionModel | FlatInteractionModelData
): data is LegacyInteractionModel => {
  return data.manifest_version === undefined || data.manifest_version === '1.0'
}

export const interactionModelDataToFlat = (
  data: LegacyInteractionModel | FlatInteractionModelData
): FlatInteractionModelData => {
  if (!isLegacyInteractionModel(data)) {
    return JSON.parse(
      JSON.stringify({ ...data, activity_values: data.activity_values || {} })
    ) as FlatInteractionModelData
  }

  const clonedData: LegacyInteractionModel = JSON.parse(JSON.stringify(data))

  const {
    uuid,
    des,
    manifest_version,
    notification_topic_arn,
    language,
    initial_step_uuid,
    blueprints,
    validation_callback,
    information_cards,
    automations,
    interaction_model_item_groups,
    success_message,
    review,
    automation_delay,
    select_and_link_target_path,
    base_interaction,
    decision_models
  } = clonedData

  const flatSteps: FlatInteractionModelData['steps'] =
    interaction_model_item_groups.map((item) =>
      removeUndefinedProperties({
        uuid: item.uuid,
        des: item.des,
        order: item.order,
        complement: item.complement,
        explanation_text: item.explanation_text,
        validation_step_callback:
          item.validation_group_callback || item.validation_step_callback,
        hide_in_review: item.hide_in_review,
        activity_models: item.activity_models.map(
          (activityModel) => activityModel.uuid
        )
      })
    )

  const result = removeUndefinedProperties({
    uuid,
    des,
    manifest_version,
    notification_topic_arn,
    language,
    initial_step_uuid,
    blueprints,
    validation_callback,
    information_cards,
    automations,
    success_message,
    review,
    automation_delay,
    select_and_link_target_path,
    decision_models,
    steps: flatSteps,
    activity_models: flatActivityModelsFromSteps(interaction_model_item_groups),
    activity_values: flatActivityValuesFromSteps(interaction_model_item_groups),
    base_interaction
  })

  return result
}
